import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PROJECT_TYPES, SALE_TYPES, PROJECT_STATUSES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, MapPin, Calendar, ChevronRight, MoreVertical, Edit2, Archive, Trash2 } from "lucide-react";
import EditProjectModal from "@/components/EditProjectModal";
import ImageUploadField from "@/components/ImageUploadField";

export default function Projects() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [form, setForm] = useState({
    name: "", project_type: "Konut", sale_type: "Topraktan Satış",
    status: "Ön Satışta", location: "", city: "", district: "",
    short_description: "", cover_image_url: "", delivery_date: "",
  });

  const canEdit = user?.role !== "agent";
  const canDelete = user?.role === "admin";
  const statusFilter = searchParams.get("status") || "";
  const visibleProjects = projects.filter((project) =>
    !statusFilter || (statusFilter === "active"
      ? ["Ön Satışta", "Devam Ediyor"].includes(project.status)
      : project.status === statusFilter)
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Proje oluşturuldu");
      setShowForm(false);
      setForm({ name: "", project_type: "Konut", sale_type: "Topraktan Satış", status: "Ön Satışta",
                location: "", city: "", district: "", short_description: "", cover_image_url: "", delivery_date: "" });
      nav(`/projects/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archiveProject = async (p) => {
    if (!window.confirm(`"${p.name}" projesini PASİFE almak istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/projects/${p.id}?force=true`);
      toast.success("Proje pasife alındı");
      setOpenMenuId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`"${p.name}" projesini SİLMEK istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Proje silindi");
      setOpenMenuId(null); load();
    } catch (err) {
      // backend returns 400 with detail object if related records exist
      const det = err?.response?.data?.detail;
      if (det && typeof det === "object" && det.detail) {
        if (window.confirm(`${det.detail}\n\nBağlı: ${det.blocks} blok, ${det.units} bölüm, ${det.sales} satış.\n\nProjeyi PASİFE almak ister misiniz?`)) {
          archiveProject(p);
        }
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  const onSaved = (updated) => {
    setProjects((arr) => arr.map((p) => p.id === updated.id ? { ...p, ...updated } : p));
    setEditingProject(null);
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-display">Projeler</h1>
          <p className="text-slate-500 mt-1">Tüm projelerinizi ve stok durumlarını yönetin.</p>
        </div>
        {canEdit && (
          <button onClick={() => setShowForm(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg px-4 py-2 inline-flex items-center gap-2 transition"
                  data-testid="new-project-btn">
            <Plus className="w-4 h-4" /> Yeni Proje
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <select value={statusFilter} onChange={(event) => {
          const value = event.target.value;
          setSearchParams(value ? { status: value } : {}, { replace: true });
        }} className="input w-auto" aria-label="Proje durumu" data-testid="project-status-filter">
          <option value="">Tüm Projeler</option>
          <option value="active">Aktif Projeler</option>
          {PROJECT_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
        {statusFilter && <span className="text-sm text-slate-500">{visibleProjects.length} proje</span>}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid="project-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Proje Adı" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="input" data-testid="project-name-input" />
            </Field>
            <Field label="Proje Tipi">
              <select value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className="input">
                {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Satış Tipi">
              <select value={form.sale_type} onChange={(e) => setForm({ ...form, sale_type: e.target.value })} className="input">
                {SALE_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Durum">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input">
                {PROJECT_STATUSES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="İl">
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input" />
            </Field>
            <Field label="İlçe">
              <input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className="input" />
            </Field>
            <Field label="Lokasyon">
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input" />
            </Field>
            <Field label="Teslim Tarihi">
              <input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="input" />
            </Field>
            <div className="md:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Kapak Görseli</div>
              <ImageUploadField value={form.cover_image_url}
                                onChange={(url) => setForm((current) => ({ ...current, cover_image_url: url }))} />
            </div>
            <div className="md:col-span-2">
              <Field label="Kısa Açıklama">
                <textarea rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                          className="input resize-none" />
              </Field>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Vazgeç</button>
            <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800" data-testid="submit-project">Kaydet</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-slate-500">Yükleniyor…</div>
      ) : visibleProjects.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">{projects.length ? "Bu durumda proje yok." : "Henüz proje yok. Yeni proje ekleyin."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="projects-grid">
          {visibleProjects.map((p) => (
            <div key={p.id} className="relative bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-amber-400 hover:shadow-md transition group"
                  data-testid={`project-card-${p.id}`}>
              {canEdit && (
                <div className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenMenuId(openMenuId === p.id ? null : p.id); }}
                          className="w-8 h-8 rounded-full bg-white/95 border border-slate-200 hover:border-amber-400 flex items-center justify-center shadow-sm"
                          data-testid={`project-menu-${p.id}`}>
                    <MoreVertical className="w-4 h-4 text-slate-600" />
                  </button>
                  {openMenuId === p.id && (
                    <div className="absolute right-0 mt-1 w-52 bg-white border border-slate-200 rounded-lg shadow-lg py-1 text-sm z-20" data-testid={`project-menu-popup-${p.id}`}>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingProject(p); setOpenMenuId(null); }}
                              className="w-full text-left px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-2"
                              data-testid={`project-edit-${p.id}`}>
                        <Edit2 className="w-4 h-4" /> Projeyi Düzenle
                      </button>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); archiveProject(p); }}
                              className="w-full text-left px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-2"
                              data-testid={`project-archive-${p.id}`}>
                        <Archive className="w-4 h-4" /> Pasife Al
                      </button>
                      {canDelete && (
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteProject(p); }}
                                className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 inline-flex items-center gap-2"
                                data-testid={`project-delete-${p.id}`}>
                          <Trash2 className="w-4 h-4" /> Sil / Arşivle
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <Link to={`/projects/${p.id}`} className="block">
                <div className="aspect-[16/9] bg-slate-100 relative">
                  {p.cover_image_url ? (
                    <img src={p.cover_image_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400">Görsel yok</div>
                  )}
                  <span className="absolute top-3 left-3 status-chip bg-white/90 text-slate-700 backdrop-blur-sm">{p.status}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-semibold text-slate-900 group-hover:text-amber-600 transition">{p.name}</h3>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-amber-500 transition shrink-0" />
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                    <MapPin className="w-3.5 h-3.5" /> {p.location || `${p.city} ${p.district}` || "—"}
                    {p.delivery_date && (<><Calendar className="w-3.5 h-3.5 ml-2" /> {p.delivery_date}</>)}
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                    <Stat label="Toplam" value={p.total_units} />
                    <Stat label="Satılan" value={p.sold_units} />
                    <Stat label="Tip" value={p.project_type} small />
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}

      {editingProject && (
        <EditProjectModal project={editingProject} onClose={() => setEditingProject(null)} onSaved={onSaved} />
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className="px-2.5 py-2 rounded-md bg-slate-50">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-semibold text-slate-900 ${small ? "text-xs" : "text-sm"}`}>{value}</div>
    </div>
  );
}

function Building2() { return null; }
